import { useState } from 'react';

export function ConnectionMethods() {
    return (
        <section id="documentation" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-20 lg:px-8" aria-labelledby="connect-heading">
            <div className="mb-12 text-center">
                <h2 id="connect-heading" className="mb-4 text-3xl font-bold text-[#F8FAFC] md:text-4xl">
                    Connect to MCP Server
                </h2>
                <p className="mx-auto max-w-2xl text-lg text-[#CBD5E1]">Choose your preferred integration method</p>
            </div>

            <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3">
                <ChatGPTMethod />
                <ClaudeAiMethod />
                <LocalConfigMethod />
            </div>

            {/* Example Usage Section */}
            <ExampleUsage />
        </section>
    );
}

function ChatGPTMethod() {
    const [copied, setCopied] = useState(false);
    const url = typeof window !== 'undefined' ? `${window.location.origin}/mcp` : 'https://your-domain.com/mcp';

    const copyToClipboard = () => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <article className="flex flex-col rounded-2xl border border-[#374151] bg-gradient-to-br from-[#2A2D35] to-[#363A42] p-8 shadow-lg transition-all hover:border-[#E81932]/30">
            <div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#10A37F] to-[#10A37F]/80 shadow-lg shadow-[#10A37F]/20">
                    <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                    </svg>
                </div>
                <h3 className="mb-3 text-xl font-bold text-[#F8FAFC]">ChatGPT</h3>
                <p className="mb-4 text-sm leading-relaxed font-semibold text-[#CBD5E1]">For Plus/Pro/Team plan users</p>
                <div className="mb-3 space-y-3">
                    <StepItem text="Go to Settings → Connected apps" color="text-[#10A37F]" />
                    <StepItem text="Add the MCP server URL below" color="text-[#10A37F]" />
                    <StepItem text="Start chatting with Turkish Airlines tools" color="text-[#10A37F]" />
                </div>
            </div>
            <div className="mt-auto">
                <div className="group relative mb-1 rounded-lg bg-[#1E293B] p-3 font-mono text-xs text-[#E2E8F0]">
                    {url}
                    <button onClick={copyToClipboard} className="absolute top-1.5 right-1.5 rounded-md bg-[#363A42]/80 p-1.5 text-[#10A37F] transition-all hover:bg-[#363A42] hover:text-[#10A37F]/80" aria-label="Copy URL">
                        {copied ? (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </article>
    );
}

function ClaudeAiMethod() {
    const [copied, setCopied] = useState(false);
    const url = typeof window !== 'undefined' ? `${window.location.origin}/mcp` : 'https://your-domain.com/mcp';

    const copyToClipboard = () => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <article className="flex flex-col rounded-2xl border border-[#374151] bg-gradient-to-br from-[#2A2D35] to-[#363A42] p-8 shadow-lg transition-all hover:border-[#E81932]/30">
            <div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#E81932] to-[#C41E3A] shadow-lg shadow-[#E81932]/20">
                    <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                        ></path>
                    </svg>
                </div>
                <h3 className="mb-3 text-xl font-bold text-[#F8FAFC]">Claude.ai</h3>
                <p className="mb-4 text-sm leading-relaxed font-semibold text-[#CBD5E1]">For Pro plan users - easiest setup</p>
                <div className="mb-3 space-y-3">
                    <StepItem text="Navigate to Settings → Integrations" />
                    <StepItem text="Add the integration URL below" />
                    <StepItem text="Start a new chat and use the tools" />
                </div>
            </div>
            <div className="mt-auto">
                <a
                    href="https://claude.ai/settings/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-4 inline-flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#E81932] transition-colors hover:text-[#C41E3A]"
                >
                    Open Claude Settings
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                    </svg>
                </a>
                <div className="group relative mb-1 rounded-lg bg-[#1E293B] p-3 font-mono text-xs text-[#E2E8F0]">
                    {url}
                    <button onClick={copyToClipboard} className="absolute top-1.5 right-1.5 rounded-md bg-[#363A42]/80 p-1.5 text-[#E81932] transition-all hover:bg-[#363A42] hover:text-[#E81932]/80" aria-label="Copy URL">
                        {copied ? (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </article>
    );
}

function LocalConfigMethod() {
    const [copied, setCopied] = useState(false);
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://your-domain.com';
    const config = `{
  "mcpServers": {
    "turkish-airlines": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "${baseUrl}/mcp"
      ]
    }
  }
}`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(config).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <article className="flex flex-col rounded-2xl border border-[#374151] bg-gradient-to-br from-[#2A2D35] to-[#363A42] p-8 shadow-lg transition-all hover:border-[#E81932]/30">
            <div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#60A5FA] to-[#60A5FA]/80 shadow-lg shadow-[#60A5FA]/20">
                    <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                    </svg>
                </div>
                <h3 className="mb-3 text-xl font-bold text-[#F8FAFC]">Desktop & IDE</h3>
                <p className="mb-4 text-sm leading-relaxed font-semibold text-[#CBD5E1]">Claude Desktop, VS Code, Cursor, and more</p>
                <div className="mb-3 space-y-3">
                    <StepItem text="Add the JSON config to your MCP settings file" color="text-[#60A5FA]" />
                    <StepItem text="Restart the application" color="text-[#60A5FA]" />
                    <StepItem text="Uses mcp-remote to bridge stdio ↔ HTTP" color="text-[#60A5FA]" />
                </div>
            </div>
            <div className="mt-auto">
                <div className="group relative mb-1 rounded-lg bg-[#1E293B] p-3 font-mono text-xs text-[#E2E8F0]">
                    <pre className="whitespace-pre-wrap">{config}</pre>
                    <button onClick={copyToClipboard} className="absolute top-1.5 right-1.5 rounded-md bg-[#363A42]/80 p-1.5 text-[#60A5FA] transition-all hover:bg-[#363A42] hover:text-[#60A5FA]/80" aria-label="Copy config">
                        {copied ? (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </article>
    );
}

function StepItem({ text, color = 'text-[#E81932]' }: { text: string; color?: string }) {
    return (
        <div className="flex items-start gap-2">
            <svg className={`h-5 w-5 ${color} mt-0.5 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
            <span className="text-sm text-[#CBD5E1]">{text}</span>
        </div>
    );
}

function ExampleUsage() {
    const examples = [
        {
            prompt: '"Show me flights from Istanbul to London tomorrow"',
            description: 'Search for available flights'
        },
        {
            prompt: '"What is the status of flight TK1?"',
            description: 'Check real-time flight status'
        },
        {
            prompt: '"Get booking details for PNR ABC123"',
            description: 'Retrieve booking information'
        }
    ];

    return (
        <div className="mx-auto mt-16 max-w-4xl">
            <div className="rounded-2xl border border-[#374151] bg-gradient-to-br from-[#2A2D35] to-[#363A42] p-8 shadow-lg md:p-10">
                <div className="mb-6 flex items-start gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#22C55E]/10">
                        <svg className="h-6 w-6 text-[#22C55E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="mb-3 text-2xl font-bold text-[#F8FAFC]">Example Prompts</h3>
                        <p className="mb-6 text-[#CBD5E1]">Once connected, try these commands with your AI assistant</p>
                        <div className="space-y-3">
                            {examples.map((example, index) => (
                                <div key={index} className="rounded-lg border border-[#CBD5E1]/10 bg-[#363A42] p-4">
                                    <p className="mb-1 font-medium text-[#F8FAFC]">{example.prompt}</p>
                                    <p className="text-sm text-[#CBD5E1]">{example.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
